import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  UserCheck,
  Mail,
  Sparkles,
  Award,
  AlertTriangle,
  Loader2,
  MessageSquare,
  Calendar,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

/** Auth helper — mirrors the pattern used elsewhere in the app. Returns
 *  the parsed JSON or throws an Error whose `.status` carries the HTTP
 *  status code and `.reason` carries the structured reason (when the
 *  backend supplies a `{ reason, message }` body) so the caller can
 *  distinguish "no trainer assigned" from "trainer exists but no
 *  published profile" without string-matching. */
async function fetchTrainerProfile() {
  const token = localStorage.getItem("ta_access_token");
  const res = await fetch(`${API_BASE}/api/me/trainer-profile`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body may not be JSON
    }
    const detail = body?.detail;
    const message =
      (detail && typeof detail === "object" && detail.message) ||
      (typeof detail === "string" && detail) ||
      `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.reason =
      (detail && typeof detail === "object" && detail.reason) || null;
    throw err;
  }
  return res.json();
}

export default function MyTrainer() {
  const { user } = useAuth();

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ["my-trainer-profile", user?.assigned_trainer_id || user?.trainer_id],
    queryFn: fetchTrainerProfile,
    enabled: !!user,
    retry: (count, err) => err?.status !== 404 && count < 2,
  });

  if (isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[60vh] gap-3"
        data-testid="my-trainer-loading"
      >
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm text-muted-foreground">Loading trainer profile…</p>
      </div>
    );
  }

  // 404 → no trainer assigned OR resolved trainer not in our users
  if (error?.status === 404) {
    if (error.reason === "trainer_user_not_found") {
      return (
        <EmptyState
          icon={AlertTriangle}
          title="Trainer Account Missing"
          body="Your trainer is on file but we can't locate their TA Fitness account. Please reach out to support and we'll re-link the assignment."
          testid="my-trainer-missing-user"
        />
      );
    }
    return (
      <EmptyState
        icon={UserCheck}
        title="No Trainer Assigned"
        body="You don't have a trainer assigned yet. Reach out to TA Fitness support and we'll match you with one."
        testid="my-trainer-empty"
      />
    );
  }

  // Any other failure → friendly fallback
  if (error) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Couldn't load trainer profile"
        body={error?.message || "Please try again in a moment."}
        testid="my-trainer-error"
      />
    );
  }

  // Stub fallback — `id === null` means "profile not yet published"
  const isStub = profile?.id === null;
  const initials = (profile?.name || profile?.email || "T")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const specialty = Array.isArray(profile?.specialty_tags) ? profile.specialty_tags : [];
  const credentials = Array.isArray(profile?.trainer_tags) ? profile.trainer_tags : [];

  return (
    <div
      className="max-w-2xl mx-auto space-y-6 pb-8"
      data-testid="my-trainer-page"
    >
      <h1 className="text-2xl font-display font-bold">My Trainer</h1>

      {/* Hero card */}
      <div
        className="rounded-3xl overflow-hidden border border-amber-400/25 bg-gradient-to-br from-amber-500/10 via-sky-500/[0.06] to-transparent shadow-[0_0_60px_-20px_rgba(56,189,248,0.4)]"
        data-testid="my-trainer-hero"
      >
        <div className="h-24 bg-gradient-to-r from-amber-500/30 via-sky-500/20 to-transparent" />
        <div className="px-6 pb-6">
          <div className="-mt-14 mb-4 flex items-end gap-4">
            {profile?.photo_url ? (
              <img
                src={profile.photo_url}
                alt={profile.name}
                data-testid="my-trainer-photo"
                className="w-24 h-24 rounded-2xl object-cover border-4 border-sidebar shadow-2xl"
              />
            ) : (
              <div
                data-testid="my-trainer-photo-fallback"
                className="w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500/40 to-sky-500/30 border-4 border-sidebar shadow-2xl flex items-center justify-center text-2xl font-display font-bold text-white"
              >
                {initials}
              </div>
            )}
            <div className="pb-2 flex-1 min-w-0">
              <h2
                className="text-xl font-display font-bold leading-tight truncate"
                data-testid="my-trainer-name"
              >
                {profile?.name || "Trainer"}
              </h2>
              {profile?.title ? (
                <p className="text-sm text-muted-foreground mt-0.5" data-testid="my-trainer-title">
                  {profile.title}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {profile?.status_text ? (
              <Badge
                variant="outline"
                className="border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                data-testid="my-trainer-status"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />
                {profile.status_text}
              </Badge>
            ) : null}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2.5 mt-5">
            <Button
              asChild
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium text-xs rounded-xl gap-2 shadow-sm"
            >
              <Link to="/chat">
                <MessageSquare className="w-3.5 h-3.5" />
                Message Trainer
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-sky-400/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-200 text-xs rounded-xl gap-2"
            >
              <Link to="/sessions">
                <Calendar className="w-3.5 h-3.5 text-sky-400" />
                Schedule Session
              </Link>
            </Button>
            {profile?.email ? (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white/90 text-xs rounded-xl gap-2"
              >
                <a href={`mailto:${profile.email}`} data-testid="my-trainer-email-link">
                  <Mail className="w-3.5 h-3.5 text-amber-400" />
                  Email
                </a>
              </Button>
            ) : null}
          </div>

          {isStub ? (
            <div className="mt-5 p-4 rounded-2xl bg-muted/20 border border-border/50">
              <p
                className="text-sm text-muted-foreground italic"
                data-testid="my-trainer-stub-notice"
              >
                Profile coming soon — your trainer hasn&apos;t published their bio
                yet. Check back later or reach out above to connect.
              </p>
              {profile?.service_status && profile.service_status !== "ok" && profile.service_status !== "profile_not_published" ? (
                <p
                  className="text-[10px] text-muted-foreground/40 mt-2 font-mono break-all"
                  data-testid="my-trainer-stub-debug"
                >
                  Status: {profile.service_status}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              {profile?.bio ? (
                <div
                  className="text-sm text-muted-foreground mt-5 leading-relaxed space-y-3 whitespace-pre-line"
                  data-testid="my-trainer-bio"
                >
                  {profile.bio
                    .split(/\n+/)
                    .filter(Boolean)
                    .map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Mission */}
      {!isStub && profile?.mission ? (
        <section
          className="rounded-2xl border border-sky-400/20 bg-sky-500/[0.04] p-5"
          data-testid="my-trainer-mission"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-sky-400" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-sky-300">
              Personal Mission
            </h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed italic">
            “{profile.mission}”
          </p>
        </section>
      ) : null}

      {/* Specialties — primary expertise tags (New Format) */}
      {specialty.length > 0 ? (
        <section
          className="rounded-2xl border border-amber-400/20 bg-amber-500/[0.04] p-5"
          data-testid="my-trainer-specialties"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-300 mb-3.5">
            Specialty Areas
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {specialty.map((s) => {
              // Parse emoji prefix if present (e.g., "🔥 Weight Loss" -> icon "🔥", label "Weight Loss")
              const m = s.match(
                /^(\p{Extended_Pictographic}\p{Emoji_Modifier}?(?:\u200d\p{Extended_Pictographic})*)\s+(.+)$/u
              );
              const icon = m ? m[1] : "💪";
              const label = m ? m[2] : s;
              return (
                <div
                  key={s}
                  className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/[0.08] border border-amber-400/20"
                  data-testid={`specialty-tag-${s}`}
                >
                  <span className="text-lg shrink-0">{icon}</span>
                  <span className="text-xs font-medium text-amber-100/90 leading-tight">
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Credentials — certifications & qualifications */}
      {credentials.length > 0 ? (
        <section
          className="rounded-2xl border border-border bg-card/40 p-5"
          data-testid="my-trainer-credentials"
        >
          <div className="flex items-center gap-2 mb-3.5">
            <Award className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Certifications & Credentials
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {credentials.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="border-white/15 bg-white/[0.04] text-white/90 px-3 py-1 rounded-lg flex items-center gap-1.5 text-xs font-normal"
                data-testid={`credential-tag-${tag}`}
              >
                <span>🏅</span>
                <span>{tag}</span>
              </Badge>
            ))}
          </div>
        </section>
      ) : null}

      {/* Direct Contact */}
      {profile?.email ? (
        <section className="rounded-2xl border border-border bg-card/40 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Direct Contact
            </h3>
            <p className="text-sm font-medium text-white/90">{profile.email}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              asChild
              size="sm"
              variant="outline"
              className="border-white/20 bg-white/[0.04] text-xs gap-2"
            >
              <a href={`mailto:${profile.email}`}>
                <Mail className="w-3.5 h-3.5 text-primary" />
                Send Email
              </a>
            </Button>
            <Button
              asChild
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs gap-2"
            >
              <Link to="/chat">
                <MessageSquare className="w-3.5 h-3.5" />
                In-App Chat
              </Link>
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function EmptyState({ icon: Icon, title, body, testid }) {
  return (
    <div
      className="max-w-xl mx-auto py-16 text-center"
      data-testid={testid}
    >
      <div className="w-20 h-20 rounded-full bg-muted/30 border border-border flex items-center justify-center mx-auto mb-4">
        <Icon className="w-9 h-9 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-display font-bold mb-2">{title}</h1>
      <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
    </div>
  );
}
